import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import ReCAPTCHA from "react-google-recaptcha";
import { useAuth } from "../context/AuthContext";
import { createSupportTicket, fetchMySupportTickets } from "../services/api";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "../components/ui";

const CATEGORY_OPTIONS = [
  { value: "support", label: "Support question" },
  { value: "billing", label: "Billing or refund" },
  { value: "bug", label: "Bug report" },
  { value: "feedback", label: "Product feedback" },
  { value: "privacy", label: "Privacy/data request" },
];

const buildDefaultForm = (user) => ({
  category: "support",
  name: user?.user_metadata?.full_name || "",
  email: user?.email || "",
  subject: "",
  message: "",
  attachment_url: "",
  honeypot: "",
});

const formatError = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const message = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item?.msg) return item.msg;
        return "";
      })
      .filter(Boolean)
      .join(" ");
    if (message) return message;
  }
  return error?.message || "Unable to submit your request right now.";
};

const Support = () => {
  const { user } = useAuth();
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const [form, setForm] = useState(() => buildDefaultForm(user));
  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      name: prev.name || user?.user_metadata?.full_name || "",
      email: user?.email || prev.email,
    }));
  }, [user?.email, user?.user_metadata?.full_name]);

  useEffect(() => {
    const load = async () => {
      if (!user?.id) return;
      setLoadingTickets(true);
      try {
        const response = await fetchMySupportTickets();
        setTickets(response?.tickets || []);
      } catch (error) {
        console.error("[support] Failed to load tickets", error);
      } finally {
        setLoadingTickets(false);
      }
    };
    load();
  }, [user?.id]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (value) => {
    setForm((prev) => ({ ...prev, category: value }));
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token || "");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.email || !form.subject || !form.message) {
      toast.error("Please fill email, subject, and message.");
      return;
    }

    const trimmedLink = form.attachment_url?.trim();
    if (trimmedLink && !/^https?:\/\/.+/i.test(trimmedLink)) {
      toast.error("Link to files must start with http:// or https://");
      return;
    }

    if (!recaptchaSiteKey) {
      toast.error("reCAPTCHA is not configured. Please try again later.");
      return;
    }

    if (!captchaToken) {
      toast.error("Please complete the reCAPTCHA challenge.");
      return;
    }

    setSubmitting(true);
    try {
      await createSupportTicket({
        category: form.category || "support",
        name: form.name || undefined,
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        attachment_url: trimmedLink || undefined,
        honeypot: form.honeypot || undefined,
        recaptcha_token: captchaToken,
      });
      toast.success("Thanks! Your request has been submitted.");
      setForm(buildDefaultForm(user));
      if (user?.id) {
        const response = await fetchMySupportTickets();
        setTickets(response?.tickets || []);
      }
    } catch (error) {
      console.error("[support] Failed to submit ticket", error);
      toast.error(formatError(error));
    } finally {
      setSubmitting(false);
      setCaptchaToken("");
      captchaRef.current?.reset();
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto w-full max-w-3xl px-6 py-16">
        <div className="mb-10">
          <Link
            to="/"
            className="inline-flex items-center text-sm font-semibold text-primary hover:underline"
          >
            ← Back to DoodleMagic
          </Link>
        </div>
        <header className="space-y-4">
          <span className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Support
          </span>
          <h1 className="font-heading text-4xl font-bold">Need a hand?</h1>
          <p className="text-base text-muted-foreground">
            We&apos;re here to help with account questions, content issues,
            privacy requests, and more.
          </p>
        </header>

        <section className="mt-10 space-y-8 text-base leading-relaxed text-muted-foreground">
          <div className="space-y-6">
            <div>
              <h3 className="font-heading text-xl font-semibold text-foreground">
                Privacy requests
              </h3>
              <p className="mt-2">
                Choose &ldquo;Privacy/data request&rdquo; to access, correct, or
                delete personal data. Submitting from the email linked to your
                account helps us verify you quickly.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-xl font-semibold text-foreground">
                Billing help
              </h3>
              <p className="mt-2">
                For pay-as-you-go questions or invoices, include your Stripe
                receipt email or reference. We don&apos;t offer refunds except
                where required by law, but we&apos;ll work to resolve billing
                issues quickly.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-xl font-semibold text-foreground">
                Product feedback
              </h3>
              <p className="mt-2">
                We love hearing how your family uses DoodleMagic! Select
                &ldquo;Product feedback&rdquo; to share ideas or report a bug.
              </p>
            </div>
          </div>

          <div
            id="request-form"
            className="rounded-2xl border border-dashed border-border/50 bg-muted/20 p-6"
          >
            <h2 className="font-heading text-2xl font-semibold text-foreground">
              Submit a support or feedback ticket
            </h2>
            <p className="mt-3">
              Share as much detail as you can. If you need to attach files,
              paste a Drive or Dropbox link.
            </p>

            <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={form.category}
                    onValueChange={handleCategoryChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Name (optional)</Label>
                  <Input
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Contact email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  name="subject"
                  required
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="Short summary"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Tell us what&apos;s going on</Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Add details so we can help faster."
                  rows={6}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="attachment_url">
                  Link to files (optional)
                </Label>
                <Input
                  id="attachment_url"
                  name="attachment_url"
                  type="url"
                  value={form.attachment_url}
                  onChange={handleChange}
                  placeholder="Google Drive / Dropbox / YouTube link"
                />
                <p className="text-xs text-muted-foreground">
                  Use an https:// link. Leave blank if you don&apos;t need to share files.
                </p>
              </div>

              {/* Honeypot field for bots */}
              <input
                type="text"
                name="honeypot"
                value={form.honeypot}
                onChange={handleChange}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
              />

          <div className="flex flex-col gap-4">
            {recaptchaSiteKey ? (
              <div className="flex justify-end">
                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey={recaptchaSiteKey}
                  onChange={handleCaptchaChange}
                  onExpired={() => setCaptchaToken("")}
                />
              </div>
            ) : (
              <p className="text-xs font-medium text-destructive">
                reCAPTCHA site key missing. Please contact support.
              </p>
            )}

            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <p>We usually reply within two business days.</p>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Submitting…" : "Submit request"}
              </Button>
            </div>
          </div>
            </form>
          </div>

          {user?.id && (
            <div className="rounded-2xl border border-border/40 bg-card p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-xl font-semibold text-foreground">
                  Your recent tickets
                </h3>
                {loadingTickets && (
                  <span className="text-xs text-muted-foreground">Loading…</span>
                )}
              </div>
              {tickets.length === 0 && !loadingTickets ? (
                <p className="mt-3 text-sm text-muted-foreground">
                  No tickets yet. Submit a request and it will appear here.
                </p>
              ) : (
                <div className="mt-4 space-y-3">
                  {tickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      className="rounded-xl border border-border/60 bg-muted/30 p-4"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-foreground">
                            {ticket.subject}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {ticket.category} ·{" "}
                            {new Date(ticket.created_at).toLocaleString()}
                          </p>
                        </div>
                        <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                          {ticket.status}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {ticket.message}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Support;

